import React, { useEffect, useLayoutEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { useHistory } from "react-router-dom";
import { useReactToPrint } from "react-to-print";
import { useRecoilState, useResetRecoilState } from "recoil";
import { useMutation, useQuery } from "@apollo/react-hooks";
import { Box, Typography } from "@mui/material";
import humanMaleFemale from "@iconify/icons-mdi/human-male-female";
import { useMyHrContractsByEmployee } from "my-hr/hooks";
import MyTaxApplicationCardInfoItem from "my-tax/components/application-card-info-item/application-card-info-item";
import ApplicationCollapseHeader from "my-tax/components/application-collapse-header";
import MyTaxApplicationHeader from "my-tax/components/application-header/application-header";
import DependentsFourChildrenCollapse from "my-tax/components/dependents-four-children-collapse";
import MyTaxFourChildrenTaxCreditApplicationPrint from "my-tax/components/four-children-tax-credit-application-print/four-children-tax-credit-application-print";
import MyTaxModifiedStatementCheck from "my-tax/components/modified-statement-check/modified-statement-check";
import TaxCreditApplicationButtonBars from "my-tax/components/tax-credit-application-buttonbars/tax-credit-application-buttonbars";
import MyTaxCreditApplicationCheckboxLine from "my-tax/components/tax-credit-application-checkbox-line/tax-credit-application-checkbox-line";
import MyTaxCreditApplicationErrorBox from "my-tax/components/tax-credit-application-error-box/tax-credit-application-error-box";
import { NOT_PROVIDED } from "my-tax/constants";
import { MY_TAX_GET_FOUR_CHILDREN_DECLARATION } from "my-tax/graphql";
import { MY_TAX_CREATE_FOUR_CHILDREN_DECLARATION } from "my-tax/graphql";
import { taxDeclarationFourChildren } from "my-tax/states/tax-declaration-four-children";
import { OcCard, OcDownload, OcMonthSelect } from "oc/components";
import {
  useOcDependentsData as useDependentsDataDI,
  useOcCurrentUserState,
  useOcSnackbar,
} from "oc/hooks";

type UseFormDataProps = {
  _id: number | string | undefined;
  setSelected: (params: any) => void;
};

const useFormData = ({ _id, setSelected }: UseFormDataProps) => {
  const { t } = useTranslation();
  const [saveErrors, setSaveErrors] = useState<string[]>([]);
  const [disabledButton, setDisabledButton] = useState(false);
  const [states, setStates] = useRecoilState(taxDeclarationFourChildren);
  const resetStates = useResetRecoilState(taxDeclarationFourChildren);

  const { data, loading, error } = useQuery(
    MY_TAX_GET_FOUR_CHILDREN_DECLARATION,
    {
      fetchPolicy: "cache-and-network",
      skip: !_id,
      variables: {
        _id: Number.parseInt(_id !== undefined ? String(_id) : "0"),
      },
    },
  );

  const result = data?.taxDeclarationFourChildren;

  // Sync query result to Recoil state (replaces deprecated onCompleted)
  useEffect(() => {
    if (!loading && result) {
      const newStates = { ...result };
      newStates.dateOfDeclaration = new Date(result?.dateOfDeclaration);
      delete newStates.__typename;
      setStates(newStates);
    }
  }, [loading, result, setStates]);

  const handleChange = ({ key, value }: { key: string; value: any }) => {
    const newState = { ...states, [key]: value };
    setStates(newState);
  };

  const { setOpenMessage } = useOcSnackbar();
  const [saveDeclaration, { loading: mutationLoading }] = useMutation(
    MY_TAX_CREATE_FOUR_CHILDREN_DECLARATION,
    {
      onCompleted: (data) => {
        const res = data?.createTaxDeclarationFourChildren;
        setDisabledButton(false);
        if (res?.result) {
          if (res?.messages?.length > 0) {
            const message = res?.messages[0];
            if (
              message === "A korábban beadott nyilatkozata törlésre került!"
            ) {
              setOpenMessage({
                type: "success",
                message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
              });
              setOpenMessage({
                type: "warning",
                message: t("MY_TAX_PREVIOUS_TAX_DECLARATION_DELETED"),
              });
            }
          } else {
            setOpenMessage({
              type: "success",
              message: t("MY_TAX_DECLARATION_CREATED_SUCCESSFULLY"),
            });
          }
          setSaveErrors([]);
          const params = {
            editable: false,
            tableId: res?.tableId,
            year: states?.dateOfDeclaration?.getFullYear().toString(),
            type: "TaxDeclarationFourChildren",
            state: "requested",
          };
          setSelected(params);
        } else {
          setSaveErrors(res?.messages);
          setDisabledButton(false);
        }
      },
      onError: () => {
        setSaveErrors([t("MY_TAX_DECLARATION_CREATED_FAILED")]);
        setDisabledButton(false);
      },
    },
  );

  useEffect(() => {
    if (!states?.isModifiedDeclaration) {
      handleStateChange({ ...states, notApplyFromMonth: "" });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [states?.isModifiedDeclaration]);

  const handleStateChange = (newState: any) => {
    setStates(newState);
  };

  const handleSave = () => {
    setDisabledButton(true);
    const params = { ...states };
    saveDeclaration({ variables: params });
  };

  return {
    data: result,
    loading: loading || mutationLoading,
    error,
    states,
    disabledButton,
    resetStates,
    handleChange,
    handleSave,
    saveErrors,
    setSaveErrors,
    handleStateChange,
  };
};

type MyTaxFourChildrenTaxCreditApplicationProps = {
  fullName: string;
  taxNumber: string;
  useDependentsData?: typeof useDependentsDataDI;
  taxApplicationSaveEnabled: boolean;
  handleRemoveRequest?: () => void;
  selected: any;
  setSelected: (params: any) => void;
  isMobile: boolean;
};

const MyTaxFourChildrenTaxCreditApplication = ({
  fullName,
  taxNumber,
  useDependentsData = useDependentsDataDI,
  taxApplicationSaveEnabled,
  handleRemoveRequest,
  selected,
  setSelected,
  isMobile,
}: MyTaxFourChildrenTaxCreditApplicationProps) => {
  const editable = selected?.editable;
  const history = useHistory();

  const {
    states,
    loading,
    error,
    saveErrors,
    disabledButton,
    setSaveErrors,
    resetStates,
    handleChange,
    handleSave,
    handleStateChange,
  } = useFormData({
    _id: selected?.tableId,
    setSelected,
  });

  const isModified = selected?.isModified;
  if (isModified && !states?.isModifiedDeclaration) {
    handleStateChange({ ...states, isModifiedDeclaration: true });
  }

  const showRemoveButton =
    states?.state === "K" || selected?.state === "K" ? true : false;

  const dateOfDeclarationString = new Date(
    states?.dateOfDeclaration,
  ).toLocaleDateString();

  const title =
    "Adóelőleg-nyilatkozat a négy vagy több gyermeket nevelő anyák kedvezményének érvényesítéséről ";

  const componentRef = useRef(null);

  const print = useReactToPrint({
    content: () => componentRef.current,
  });

  const handlePrint = () => {
    print();
  };

  const { data: dependents } = useDependentsData();

  useEffect(() => {
    if (editable) {
      dependents?.forEach((item: any) => {
        const dateOfBirth = item.birthDate ? new Date(item.birthDate) : null;
        if (dateOfBirth && dateOfBirth.toString() !== "Invalid Date") {
          const newItem = {
            dependentSerialNumber: item.itemSerial,
            name: item.name,
            taxNumber: item.taxNumber,
            placeOfBirth: item.birthPlace,
            dateOfBirth: dateOfBirth.toLocaleDateString(),
            mothersName: item.mothersName,
          };
          const isExist = states?.dependents.find(
            (item: any) =>
              item.dependentSerialNumber === newItem.dependentSerialNumber,
          );
          if (!isExist) {
            const dependents = states?.dependents;
            const newDependents = [...dependents, newItem];
            handleChange({ key: "dependents", value: newDependents });
          }
        }
      });
    }
  }, [dependents, editable, handleChange, states?.dependents]);

  const { t } = useTranslation();

  const handleCancel = () => {
    // Only reset states when in edit mode
    if (editable) {
      resetStates();
    }
    setSaveErrors([]);
  };

  useEffect(() => {
    if (states?.isModifiedDeclaration === false) {
      handleChange({ key: "notApplyFromMonth", value: "" });
    }
    return () => {
      handleCancel();
    };
  }, []); // eslint-disable-line

  let { contractCode, employeeCode } = useOcCurrentUserState();
  const { data: contractsData } = useMyHrContractsByEmployee({
    employee: employeeCode,
  });

  let selectedContract = contractsData?.find(
    (item: any) => item.code === contractCode,
  );

  let disabled = true;

  const handleModChange = ({ key, value }: { key: string; value: any }) => {
    handleChange({ key, value });
  };

  const handleScrollToError = () => {
    const errorElement = document.getElementById("errorBox");
    if (errorElement) {
      window.scrollTo({
        top: 300,
        behavior: "smooth",
      });
    }
  };
  useLayoutEffect(() => {
    handleScrollToError();
  }, [saveErrors, history.action]);

  useEffect(() => {
    // Only reset when creating a NEW declaration (editable=true AND no tableId)
    if (editable && !selected?.tableId) {
      resetStates();
    }
    if (history.action === "POP" && editable) {
      return handleCancel();
    }
  }, []); //eslint-disable-line

  return (
    <>
      <Box
        style={{
          marginBottom: "-15px",
        }}
      >
        <Box
          sx={{
            display: "none",
            displayPrint: "block",
            paddingRight: "85px",
          }}
        >
          <MyTaxFourChildrenTaxCreditApplicationPrint
            ref={componentRef}
            fullName={fullName}
            taxNumber={taxNumber}
            employerName={selectedContract?.company?.name || ""}
            employerTaxNumber={selectedContract?.company?.taxNumber || ""}
            states={states}
            applicationDate={dateOfDeclarationString}
          />
        </Box>
        <MyTaxApplicationHeader
          title={title}
          onCancel={handleCancel}
          isMobile={isMobile}
        />
        <Box
          padding="16px"
          display="flex"
          flexDirection="row"
          justifyContent="center"
        >
          <Box
            width="260px"
            style={{
              padding: "8px",
              borderRadius: "4px",
            }}
          >
            <OcDownload
              label="Tájékoztató"
              iconType="document"
              actionIconType="url"
              href={"#/my-hr/four-children-tax-credit-information"}
              target="_blank"
              isSmall={true}
            />
          </Box>
        </Box>
        {saveErrors.length > 0 && (
          <Box id="errorBox">
            <MyTaxCreditApplicationErrorBox error={saveErrors} />
          </Box>
        )}
        <OcCard
          label="I. A nyilatkozatot adó magánszemély"
          loading={loading}
          error={!!error}
        >
          <Box
            style={{
              padding: "0px 17px",
            }}
          >
            <Box height="52px" />
            <MyTaxApplicationCardInfoItem
              label="Dátum"
              value={dateOfDeclarationString}
              isMobile={isMobile}
            />
            <MyTaxApplicationCardInfoItem
              label="Dolgozó neve"
              value={fullName}
              isMobile={isMobile}
            />
            <MyTaxApplicationCardInfoItem
              label="Adóazonosító jele"
              value={taxNumber}
              isMobile={isMobile}
            />
          </Box>
          <Box height="15px" />
          <MyTaxModifiedStatementCheck
            label={t("MY_TAX_MODIFIED_APPLICATION")}
            name="isModifiedDeclaration"
            value={states?.isModifiedDeclaration}
            onChange={handleModChange}
            disabled={!editable || isModified}
            isMobile={isMobile}
          />
          <Box height="15px" />
          <Box display="flex" flexDirection="row">
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              1.
            </Typography>
            <Box width="16px" />
            <Typography variant="body1" color={editable ? "primary" : "info"}>
              Gyermek neve, adóazonosító jele
            </Typography>
          </Box>
          <Box height="36px" />
          {states?.dependents.length !== 0 ? (
            states?.dependents?.map((item: any, index: number) => {
              return (
                <Box key={`dependents-${index}`}>
                  <DependentsFourChildrenCollapse
                    index={index}
                    data={item}
                    isMobile={isMobile}
                  />
                  <Box height="36px" />
                </Box>
              );
            })
          ) : (
            <Box>
              <ApplicationCollapseHeader
                dependentName={NOT_PROVIDED}
                dependentTaxNumber=""
                expanded={disabled}
                icon={humanMaleFemale}
                taxNumberLabel=""
                handleExpandClick={() => { }}
                isMobile={isMobile}
              />
              <Box height="36px" />
            </Box>
          )}
          <MyTaxCreditApplicationCheckboxLine
            id="isApplyNetak"
            type="checkbox"
            prefix="2."
            checked={states?.isEligible}
            checkboxDisabled={!editable}
            onChange={(e) =>
              handleChange({ key: "isEligible", value: e.target.checked })
            }
            text="Nyilatkozom, hogy jogosult vagyok a négy vagy több gyermeket
                nevelő anyák kedvezményére, azt igénybe kívánom venni."
            editable={editable}
          />
          <Box height="36px" />
          <MyTaxCreditApplicationCheckboxLine
            id="isApplyNetakPermanently"
            type="checkbox"
            prefix="3."
            checked={states?.permanentlyEntitled}
            checkboxDisabled={!editable}
            onChange={(e) =>
              handleChange({
                key: "permanentlyEntitled",
                value: e.target.checked,
              })
            }
            text="Nyilatkozatomat visszavonásig kérem figyelembe venni (folytatólagos nyilatkozatot teszek)."
            editable={editable}
          />
          <Box height="36px" />
          <Box>
            <Box display="flex" flexDirection="row">
              <Typography
                variant="body1"
                color={
                  editable && states?.isModifiedDeclaration ? "primary" : "info"
                }
              >
                4.
              </Typography>
              <Box width="16px" />
              <Typography
                variant="body1"
                color={
                  editable && states?.isModifiedDeclaration ? "primary" : "info"
                }
              >
                Nyilatkozom, hogy a négy vagy több gyermeket nevelő anyák
                kedvezményét
              </Typography>
            </Box>
            <Box height="16px" />
            <Box
              display="flex"
              flexDirection={isMobile ? "column" : "row"}
              alignItems="center"
              style={{ marginLeft: isMobile ? "0px" : "30px" }}
            >
              <OcMonthSelect
                id="notApplyFromMonth"
                startMonth={1}
                width="160px"
                label={t("MY_TAX_TAX_CREDIT_MONTH")}
                disabled={!states?.isModifiedDeclaration || !editable}
                selectedMonth={states?.notApplyFromMonth}
                onSelectMonth={(e) =>
                  handleChange({ key: "notApplyFromMonth", value: e })
                }
                monthList={[
                  { index: "1", name: "Január" },
                  { index: "2", name: "Február" },
                  { index: "3", name: "Március" },
                  { index: "4", name: "Április" },
                  { index: "5", name: "Május" },
                  { index: "6", name: "Június" },
                  { index: "7", name: "Július" },
                  { index: "8", name: "Augusztus" },
                  { index: "9", name: "Szeptember" },
                  { index: "10", name: "Október" },
                  { index: "11", name: "November" },
                  { index: "12", name: "December" },
                ]}
              />
              <Box width="16px" />
              <Typography
                variant="body1"
                color={
                  editable && states?.isModifiedDeclaration ? "primary" : "info"
                }
              >
                hónaptól kezdődően nem (erre a hónapra sem) kívánom igénybe
                venni.
              </Typography>
            </Box>
          </Box>
          <Box height="36px" />
        </OcCard>
        <Box height="16px" />
        <TaxCreditApplicationButtonBars
          taxApplicationSaveEnabled={editable && taxApplicationSaveEnabled}
          cancelData={handleCancel}
          handlePrint={handlePrint}
          saveData={handleSave}
          editable={editable}
          disabledButton={disabledButton}
          handleRemoveRequest={handleRemoveRequest ?? (() => { })}
          showRemoveButton={showRemoveButton}
          isModified={isModified}
          isModifiedDeclaration={states?.isModifiedDeclaration}
          isMobile={isMobile}
        />
      </Box>
    </>
  );
};

export default MyTaxFourChildrenTaxCreditApplication;
